import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch, changeLocation, RootState, getClient, bearerAuth, validationErrors } from '@src/core/store';
import { setCurrentSale, setProcessedSale, loadSaleAsCurrent } from './sale';
import { setCurrentOrder, setProcessedOrder, loadOrderAsCurrent } from './order';
import { setDirectLink, setProcessedDirectLink } from './directLink';
import handleExternalAuthentication from '@src/common/externalAuthentication';
import Bugsnag from '@bugsnag/browser';
import { clearSaleOrigin } from '@src/common';
import { publicApiTokenSelector } from './auth';
import * as storage from '../common/storage';
import {
  Order,
  Sale,
  DirectLink,
  TransactionRequest,
  PaymentCardParams,
  PaymentMethodExternalAuthentication,
  PaymentOption,
  Transaction,
} from '@src/core/buyerApiClient';
import { AxiosResponse, AxiosError } from 'axios';
import { globalParameterSelector } from './globalParameters';

const planneWidgetSaleReturnKey = 'planneWidgetSaleReturn';

export const BRASPAG_3DS_STATUS = {
  success: 'success',
  failure: 'failure',
  unenrolled: 'unenrolled',
  disabled: 'disabled',
  error: 'error',
  unsuportedBrand: 'unsuportedBrand',
};

export interface PaymentState {
  paymentOptions: { [key: string]: PaymentOptionsLoad };
  paymentOngoing: boolean;
  paymentErrors: { [key: string]: string[] };
  lastSuccessfulPayment?: number;
  skipExternalAuth: boolean;
}

interface PaymentOptionsLoad {
  list: PaymentOption[];
  loading: boolean;
}

const initialState: PaymentState = {
  paymentOptions: {},
  paymentOngoing: false,
  paymentErrors: {},
  skipExternalAuth: false,
};

// Reducers

const ANTI_FRAUD_ERROR_CODE = 'AntiFraud';
const TRANSACTION_ERROR_CODE = 'Transaction';

export const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    setPaymentOptionsLoad: {
      reducer(
        state,
        {
          payload: list,
          meta: { key, loading },
        }: PayloadAction<PaymentOption[], string, { key: string; loading: boolean }>,
      ) {
        state.paymentOptions[key] = { list, loading };
      },
      prepare(pms: PaymentOption[], key: string, loading = false) {
        return { payload: pms, meta: { key, loading } };
      },
    },
    setPaymentOngoing: (state: PaymentState, action: PayloadAction<boolean, string>) => {
      state.paymentOngoing = action.payload;
    },
    setPaymentErrors: (state: PaymentState, action: PayloadAction<{ [key: string]: string[] }, string>) => {
      state.paymentErrors = action.payload;
    },
    setLastSuccessfulPayment: (state: PaymentState, action: PayloadAction) => {
      state.lastSuccessfulPayment = Date.now();
    },
    setSkipExternalAuth: (state: PaymentState, action: PayloadAction<boolean>) => {
      state.skipExternalAuth = action.payload;
    },
    triggerExternalAuthStatus: (_: PaymentState, __: PayloadAction<string, string>) => {},
    triggerExternalAuthError: () => {},
  },
});

export const {
  setPaymentOptionsLoad,
  setPaymentOngoing,
  setPaymentErrors,
  setLastSuccessfulPayment,
  setSkipExternalAuth,
  triggerExternalAuthStatus,
  triggerExternalAuthError,
} = paymentSlice.actions;

// Selectors

export const paymentOngoingSelector = (state: RootState) =>
  state.payment.paymentOngoing ||
  (state.payment.lastSuccessfulPayment != null && Date.now() - state.payment.lastSuccessfulPayment < 10000); // Workaround to keep displaying the loading spinner for a while after a successful payment, so things can load

export const paymentErrorsSelector = (state: RootState) => state.payment.paymentErrors;

// Actions

export const clearPaymentErrors = () => (dispatch: AppDispatch) => dispatch(setPaymentErrors({}));

export interface ExternalAuthResult {
  proceed: boolean;
  error: string;
  externalAuthentication: any;
}

const externalAuthDecision = async (externalAuthData: any): Promise<ExternalAuthResult> => {
  const { widgetUrl, paymentMethod, sale, directLink, data, attempts, dispatch, retrying = false } = externalAuthData;

  const extAuthData = await handleExternalAuthentication(widgetUrl, paymentMethod, sale, data);
  const result: ExternalAuthResult = {
    proceed: false,
    error: '',
    externalAuthentication: null,
  };

  if (attempts < 1 && retrying) {
    dispatch(setSkipExternalAuth(true));
    result.error = 'Houve um erro ao processar sua transação. Por favor, aguarde alguns instantes e tente novamente.';
    return result;
  }

  if (extAuthData) {
    dispatch(triggerExternalAuthStatus(extAuthData.data.status));
    console.log(`External auth status: ${extAuthData.data.status}`);

    Bugsnag.notify({
      name: 'ExternalAuthStatus',
      message: `Sale ${sale.id} got status ${extAuthData.data.status} in 3DS attempt`,
    });

    switch (extAuthData.data.status) {
      case BRASPAG_3DS_STATUS.success:
        result.proceed = true;
        result.externalAuthentication = extAuthData.data.e;
        break;

      case BRASPAG_3DS_STATUS.error:
        dispatch(triggerExternalAuthError());
        return externalAuthDecision({
          widgetUrl,
          paymentMethod,
          sale,
          directLink,
          data,
          attempts: attempts - 1,
          dispatch,
          retrying: true,
        });

      case BRASPAG_3DS_STATUS.failure:
        result.error = 'Compra reprovada, verifique se os dados estão corretos, ou utilize outro meio de pagamento';
        result.proceed = false;
        break;

      case BRASPAG_3DS_STATUS.disabled:
      case BRASPAG_3DS_STATUS.unsuportedBrand:
      case BRASPAG_3DS_STATUS.unenrolled:
      default:
        result.proceed = true;
        break;
    }
  } else {
    result.proceed = true;
  }

  return result;
};

export interface Payment {
  order?: Order;
  sale?: Sale;
  directLink?: DirectLink;
  paymentCard?: PaymentCardParams;
  paymentMethodId?: string;
  externalAuthentication?: PaymentMethodExternalAuthentication;
}

const getErrorMessageByType = (error: AxiosError<any>) => {
  const statusCode = error.response?.status;
  switch (statusCode) {
    case 400:
      const errorType = error.response!.data?.errors[0]?.extra?.type;
      switch (errorType) {
        case ANTI_FRAUD_ERROR_CODE:
          return 'checkout_payment_anti_fraud_error';
        case TRANSACTION_ERROR_CODE:
          return 'checkout_payment_transaction_error';
        default:
          return 'checkout_payment_generic_transaction_error';
      }
    case 401:
    case 403:
      return 'checkout_payment_unauthorized_error';
    default:
      return 'checkout_payment_generic_transaction_error';
  }
};

export const processPayment = (payment: Payment) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const { order, sale, directLink, paymentMethodId, paymentCard } = payment;
  const state = getState();
  const {
    payment: { paymentOngoing },
    auth: { buyerToken },
  } = state;

  if (paymentOngoing || ((order || sale) && buyerToken == null)) {
    return;
  }

  const sessionId = globalParameterSelector('session-id')(state);

  const data: TransactionRequest = {
    paymentMethodId: paymentMethodId!,
    paymentCard,
    returnUrl: location.href,
    deviceInfo: {
      language: navigator.language,
      screenHeight: screen.height,
      screenWidth: screen.width,
      timeZoneOffset: new Date().getTimezoneOffset() / 60,
    },
    sessionId,
  };

  dispatch(setPaymentOngoing(true));
  dispatch(setPaymentErrors({}));

  // TODO return this back in the future
  //if (payment.sale?.paymentMethods) {
  //const paymentMethod = payment.sale?.paymentMethods?.find((pm) => pm.id == paymentMethodId);

  //if (paymentMethod && paymentMethod.externalAuthentication && !state.sale.skipExternalAuth) {
  //const attempts = 2;
  //const { proceed, error, externalAuthentication } = await externalAuthDecision({
  //widgetUrl: state.userPreferences.widgetUrl,
  //paymentMethod,
  //sale,
  //directLink,
  //data,
  //attempts,
  //dispatch,
  //retrying: false,
  //});

  //if (externalAuthentication) {
  //data.externalAuthentication = externalAuthentication;
  //}

  //if (!proceed) {
  //dispatch(setPaymentOngoing(false));
  //dispatch(setPaymentErrors({ transactionError: [error] }));
  //return;
  //}
  //}
  //}

  try {
    if (sale) {
      let response: AxiosResponse<Transaction>;
      try {
        response = await getClient(state).transactions.transactSale(sale.id, data, bearerAuth(buyerToken!));
      } catch (err) {
        const errors = validationErrors(err);
        if (errors) {
          dispatch(setPaymentErrors(errors));
          dispatch(getPaymentStatus(payment)); // Reload in case the sale is already expired
          return;
        }

        const errorMessage = getErrorMessageByType(err as AxiosError<any>);
        dispatch(
          setPaymentErrors({
            transactionError: [errorMessage],
          }),
        );
        return;
      }

      if (response.data.pending && response.data.paymentUrl) {
        const publicApiKey = publicApiTokenSelector(state);

        storage.setValue(publicApiKey!, planneWidgetSaleReturnKey, `${sale.id}`);
        dispatch(changeLocation(response.data.paymentUrl));
      } else {
        loadSaleAsCurrent(dispatch, getState, sale.id, setProcessedSale);
        clearSaleOrigin();
        if (response.data.succeeded) {
          dispatch(setLastSuccessfulPayment());
        }
      }
    } else if (order) {
      let response: AxiosResponse<Transaction>;
      try {
        response = await getClient(state).transactions.transactOrder(order.id, data, bearerAuth(buyerToken!));
      } catch (err) {
        const errors = validationErrors(err);
        if (errors) {
          dispatch(setPaymentErrors(errors));
          dispatch(getPaymentStatus(payment)); // Reload in case the order is already expired
          return;
        }

        const errorMessage = getErrorMessageByType(err as AxiosError<any>);
        dispatch(
          setPaymentErrors({
            transactionError: [errorMessage],
          }),
        );
        return;
      }

      if (response.data.pending && response.data.paymentUrl) {
        const publicApiKey = publicApiTokenSelector(state);

        storage.setValue(publicApiKey!, planneWidgetSaleReturnKey, `${order.id}`);
        dispatch(changeLocation(response.data.paymentUrl));
      } else {
        loadOrderAsCurrent(dispatch, getState, order.id, setProcessedOrder);
        clearSaleOrigin();
        if (response.data.succeeded) {
          dispatch(setLastSuccessfulPayment());
        }
      }
    } else if (directLink) {
      let response: AxiosResponse<Transaction>;
      try {
        response = await getClient(state).transactions.transactDirectLink(directLink.id, data);
      } catch (err) {
        const errors = validationErrors(err);
        if (errors) {
          dispatch(setPaymentErrors(errors));
          return;
        }

        const errorMessage = getErrorMessageByType(err as AxiosError<any>);
        dispatch(
          setPaymentErrors({
            transactionError: [errorMessage],
          }),
        );
        return;
      }

      if (response.data.pending && response.data.paymentUrl) {
        const publicApiKey = publicApiTokenSelector(state);

        storage.setValue(publicApiKey!, planneWidgetSaleReturnKey, `${directLink.code}`);
        dispatch(changeLocation(response.data.paymentUrl));
      } else {
        if (response.data.succeeded) {
          dispatch(setLastSuccessfulPayment());
        }

        const { data: newDirectLink } = await getClient(getState()).directLinks.getDirectLink(directLink.code);

        if (newDirectLink) {
          dispatch(setProcessedDirectLink(newDirectLink));
          clearSaleOrigin();
        }
      }
    }
  } finally {
    dispatch(setPaymentOngoing(false));
  }
};

export const getPaymentStatus = (payment: Payment) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const { order, sale, directLink } = payment;

  const state = getState();
  const {
    auth: { buyerToken },
  } = state;

  if ((order || sale) && buyerToken == null) {
    return;
  }

  if (order) {
    loadOrderAsCurrent(dispatch, getState, order.id, setCurrentOrder);
  } else if (sale) {
    loadSaleAsCurrent(dispatch, getState, sale.id, setCurrentSale);
  } else if (directLink) {
    const { data: newDirectLink } = await getClient(state).directLinks.getDirectLink(directLink.code);
    dispatch(setDirectLink(newDirectLink));
  }
};

export interface Transactable {
  order?: Order;
  sale?: Sale;
  directLink?: DirectLink;
}

const keyFromTransactable = (tr: Transactable) => {
  if (tr.order) {
    return `order_${tr.order.id}`;
  } else if (tr.sale) {
    return `sale_${tr.sale.id}`;
  } else if (tr.directLink) {
    return `directLink_${tr.directLink.code}`;
  }
  return '';
};

export const loadPaymentOptions = (tr: Transactable) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const { order, sale, directLink } = tr;

  const state = getState();
  const {
    auth: { buyerToken },
  } = state;

  if ((sale || order) && buyerToken == null) {
    return;
  }

  const trKey = keyFromTransactable(tr);
  dispatch(setPaymentOptionsLoad([], trKey, true));

  const sessionId = globalParameterSelector('session-id')(state);

  // Ideally, should be paginated, but is unlikely we'll have more than 100 payment options
  if (tr.order) {
    const { data: pms } = await getClient(state).paymentOptions.getOrderPaymentOptions(
      tr.order.id,
      sessionId,
      0,
      100,
      bearerAuth(buyerToken!),
    );
    dispatch(setPaymentOptionsLoad(pms, trKey, false));
  } else if (sale) {
    const { data: pms } = await getClient(state).paymentOptions.getSalePaymentOptions(
      sale.id,
      sessionId,
      0,
      100,
      bearerAuth(buyerToken!),
    );
    dispatch(setPaymentOptionsLoad(pms, trKey, false));
  } else if (directLink) {
    const { data: pms } = await getClient(state).paymentOptions.getDirectLinkPaymentOptions(
      directLink.id,
      sessionId,
      0,
      100,
    );
    dispatch(setPaymentOptionsLoad(pms, trKey, false));
  }
};

export const paymentOptionSelector = (tr: Transactable) => (state: RootState) => {
  const trKey = keyFromTransactable(tr);
  return state.payment.paymentOptions[trKey];
};

export default paymentSlice.reducer;
